// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';

// Material library
import { MatIconModule } from '@angular/material/icon';

// Third party
import { Subscription } from 'rxjs';

// Local library
import { NumberWithCommasPipe,
TranslatePipe
} from '@amwager/util';
import {
  BetSummaryService,
  IBetSummaryTotals
} from '@amwager/wager/data-access';

/**
 * Bet Summary floating panel — renders six totals from BetSummaryService
 */
@Component({
  selector: 'feature-mat-bet-summary',
  templateUrl: './bet-summary.component.html',
  styleUrls: ['./bet-summary.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    NumberWithCommasPipe,
    TranslatePipe
  ]
})
export class BetSummaryComponent implements OnInit, OnDestroy {

  // Emits when the user clicks the panel's close button
  @Output() doClose = new EventEmitter<void>();

  totals: IBetSummaryTotals = {
    stackTodaySum: '0.00',
    betTodaySum: '0.00',
    wonTodaySum: '0.00',
    profitLossSum: '0.00',
    activeBetsSum: '0.00',
    realTimePlSum: '0.00'
  };

  private totalsSub?: Subscription;

  constructor(
    private readonly betSummaryService: BetSummaryService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  /**
   * Subscribe to totals stream on init
   * @returns {void}
   */
  ngOnInit(): void {
    this.totalsSub = this.betSummaryService.totals$.subscribe((totals: IBetSummaryTotals) => {
      this.totals = totals;
      this.cdRef.markForCheck();
    });
  }

  /**
   * Unsubscribe totals stream on destroy
   * @returns {void}
   */
  ngOnDestroy(): void {
    this.totalsSub?.unsubscribe();
  }

  /**
   * Emit close event to the parent
   * @returns {void}
   */
  close(): void {
    this.doClose.emit();
  }

  /**
   * Check if value is negative (e.g. `-12.34`)
   * @param {string} value pre-formatted total
   * @returns {boolean} true when value is negative
   */
  isNegative(value: string): boolean {
    return typeof value === 'string' && value.trim().startsWith('-');
  }

  /**
   * Check if value is strictly positive (excludes zero)
   * @param {string} value pre-formatted total
   * @returns {boolean} true when value represents a gain
   */
  isPositive(value: string): boolean {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (trimmed.startsWith('-')) return false;
    return parseFloat(trimmed) > 0;
  }
}
